import { useEffect, useState } from "react";
import PermitTypeView from "../components/PermitTypeView";
import { OrgRole } from "../models/OrgRole";
import { PermitTypeOrgRole } from "../models/PermitTypeOrgRole";
import { PermitTypeDocumentType } from "../models/PermitTypeDocumentType";
import { DocumentType } from "../models/DocumentType";
import { PermitRoleSettingsInput } from "../models/PermitRoleSettingsInput";
import { useAddRoleToPermitType } from "../operations/mutations/addRoleToPermitType";
import { useRemoveRoleFromPermitType } from "../operations/mutations/removeRoleFromPermitType";
import { useSetPermitTypeRoleSettings } from "../operations/mutations/setPermitTypeRoleSettings";
import { useAddDocumentTypeToPermitType } from "../operations/mutations/addDocumentTypeToPermitType";
import { useRemoveDocumentTypeFromPermitType } from "../operations/mutations/removeDocumentTypeFromPermitType";
import { useSetPermitTypeDocumentTypeSettings } from "../operations/mutations/setPermitTypeDocumentTypeSettings";
import AddRoleToPermitTypeModal from "../components/AddRoleToPermitTypeModal";
import EditPermitTypeRoleSettingsModal from "../components/EditPermitTypeRoleSettingsModal";
import AddDocumentTypeToPermitTypeModal from "../components/AddDocumentTypeToPermitTypeModal";
import EditPermitTypeDocumentTypeSettingsModal from "../components/EditPermitTypeDocumentTypeSettingsModal";
import { DocumentTypeSettingsInput } from "../models/globalTypes";
import { toastSuccess } from "../utils";
import { useLazyGetPermitTypeById } from "../operations/queries/getPermitTypeById";

interface Props {
  permitTypeId?: string;
  organizationId: string;
  allOrgRoles: OrgRole[];
  allDocumentTypes: DocumentType[];
  onClose: () => void;
}

const PermitTypeViewContainer = ({ permitTypeId, organizationId, allOrgRoles, allDocumentTypes, onClose }: Props) => {
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
  const [addDocumentTypeModalOpen, setAddDocumentTypeModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [docTypeSettingsModalOpen, setDocTypeSettingsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<PermitTypeOrgRole | undefined>(undefined);
  const [selectedDocumentType, setSelectedDocumentType] = useState<PermitTypeDocumentType | undefined>(undefined);

  const { mutate: addRole, status: addRoleStatus } = useAddRoleToPermitType();
  const { mutate: removeRole, status: removeRoleStatus } = useRemoveRoleFromPermitType();
  const { mutate: setSettings, status: setSettingsStatus } = useSetPermitTypeRoleSettings();
  const { mutate: addDocType, status: addDocTypeStatus } = useAddDocumentTypeToPermitType();
  const { mutate: removeDocType, status: removeDocTypeStatus } = useRemoveDocumentTypeFromPermitType();
  const { mutate: setDocTypeSettings, status: setDocTypeSettingsStatus } = useSetPermitTypeDocumentTypeSettings();

  const { getPermitTypeById, data: permitType, refetch: refetchPermitType } = useLazyGetPermitTypeById();

  useEffect(() => {
    if (permitTypeId && organizationId) {
      getPermitTypeById({ variables: { organizationId, permitTypeId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitTypeId, organizationId]);

  const permitTypeRoles = permitType?.roles ?? [];
  const permitTypeDocumentTypes = permitType?.documentTypes ?? [];

  // Refetch after successful mutations
  useEffect(() => {
    if (addRoleStatus.successful && permitTypeId) {
      toastSuccess("Role added to permit type.");
      setAddRoleModalOpen(false);
      addRoleStatus.reset();
      refetchPermitType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRoleStatus.successful]);

  useEffect(() => {
    if (removeRoleStatus.successful && permitTypeId) {
      toastSuccess("Role removed from permit type.");
      removeRoleStatus.reset();
      refetchPermitType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeRoleStatus.successful]);

  useEffect(() => {
    if (setSettingsStatus.successful && permitTypeId) {
      toastSuccess("Role settings updated.");
      setSettingsModalOpen(false);
      setSelectedRole(undefined);
      setSettingsStatus.reset();
      refetchPermitType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSettingsStatus.successful]);

  useEffect(() => {
    if (addDocTypeStatus.successful && permitTypeId) {
      toastSuccess("Document type added to permit type.");
      setAddDocumentTypeModalOpen(false);
      addDocTypeStatus.reset();
      refetchPermitType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDocTypeStatus.successful]);

  useEffect(() => {
    if (removeDocTypeStatus.successful && permitTypeId) {
      toastSuccess("Document type removed from permit type.");
      removeDocTypeStatus.reset();
      refetchPermitType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeDocTypeStatus.successful]);

  useEffect(() => {
    if (setDocTypeSettingsStatus.successful && permitTypeId) {
      toastSuccess("Document type settings updated.");
      setDocTypeSettingsModalOpen(false);
      setSelectedDocumentType(undefined);
      setDocTypeSettingsStatus.reset();
      refetchPermitType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDocTypeSettingsStatus.successful]);

  const assignedOrgRoleIds = new Set(permitTypeRoles.map((r) => r.orgRole.id));
  const availableOrgRoles = allOrgRoles.filter((r) => !assignedOrgRoleIds.has(r.id));

  const assignedDocTypeIds = new Set(permitTypeDocumentTypes.map((d) => d.documentType.id));
  const availableDocumentTypes = allDocumentTypes.filter((d) => !assignedDocTypeIds.has(d.id));

  const handleAddRoles = (orgRoleIds: string[]) => {
    if (!permitTypeId) return;
    // Submit each selected role sequentially; for simplicity issue one at a time
    orgRoleIds.forEach((orgRoleId) => {
      addRole({ variables: { organizationId, permitTypeId, orgRoleId } });
    });
  };

  const handleRemoveRole = (role: PermitTypeOrgRole) => {
    if (!permitTypeId) return;
    removeRole({ variables: { organizationId, permitTypeId, orgRoleId: role.orgRole.id } });
  };

  const handleOpenSettings = (role: PermitTypeOrgRole) => {
    setSelectedRole(role);
    setSettingsModalOpen(true);
  };

  const handleSettingsSubmit = (orgRoleId: string, permitRoleSettingsInput: PermitRoleSettingsInput | null) => {
    if (!permitTypeId) return;
    setSettings({ variables: { organizationId, permitTypeId, orgRoleId, permitRoleSettingsInput } });
  };

  const handleSettingsModalClose = () => {
    setSettingsModalOpen(false);
    setSelectedRole(undefined);
    setSettingsStatus.reset();
  };

  const handleAddRoleModalClose = () => {
    setAddRoleModalOpen(false);
    addRoleStatus.reset();
  };

  const handleAddDocumentTypes = (documentTypeIds: string[]) => {
    if (!permitTypeId) return;
    documentTypeIds.forEach((documentTypeId) => {
      addDocType({ variables: { organizationId, permitTypeId, documentTypeId } });
    });
  };

  const handleRemoveDocumentType = (item: PermitTypeDocumentType) => {
    if (!permitTypeId) return;
    removeDocType({ variables: { organizationId, permitTypeId, documentTypeId: item.documentType.id } });
  };

  const handleOpenDocumentTypeSettings = (item: PermitTypeDocumentType) => {
    setSelectedDocumentType(item);
    setDocTypeSettingsModalOpen(true);
  };

  const handleDocTypeSettingsSubmit = (
    documentTypeId: string,
    documentTypeSettingsInput: DocumentTypeSettingsInput | null,
  ) => {
    if (!permitTypeId) return;
    setDocTypeSettings({
      variables: { organizationId, permitTypeId, documentTypeId, documentTypeSettingsInput },
    });
  };

  const handleDocTypeSettingsModalClose = () => {
    setDocTypeSettingsModalOpen(false);
    setSelectedDocumentType(undefined);
    setDocTypeSettingsStatus.reset();
  };

  const handleAddDocumentTypeModalClose = () => {
    setAddDocumentTypeModalOpen(false);
    addDocTypeStatus.reset();
  };

  return (
    <>
      <PermitTypeView
        permitType={permitType ?? undefined}
        permitTypeRoles={permitTypeRoles}
        permitTypeDocumentTypes={permitTypeDocumentTypes}
        rolesLoading={!permitTypeRoles}
        documentTypesLoading={!permitTypeDocumentTypes}
        availableOrgRoles={availableOrgRoles}
        availableDocumentTypes={availableDocumentTypes}
        onClose={onClose}
        onOpenAddRoleModal={() => setAddRoleModalOpen(true)}
        onOpenAddDocumentTypeModal={() => setAddDocumentTypeModalOpen(true)}
        onRoleSettings={handleOpenSettings}
        onRemoveRole={handleRemoveRole}
        removeRoleStatus={removeRoleStatus}
        onAddRoles={handleAddRoles}
        onAddDocumentTypes={handleAddDocumentTypes}
        onDocumentTypeSettings={handleOpenDocumentTypeSettings}
        onRemoveDocumentType={handleRemoveDocumentType}
      />

      <AddRoleToPermitTypeModal
        open={addRoleModalOpen}
        onClose={handleAddRoleModalClose}
        onSubmit={handleAddRoles}
        submitInProgress={addRoleStatus.loading}
        submitErrors={addRoleStatus.errors}
        availableOrgRoles={availableOrgRoles}
      />

      <EditPermitTypeRoleSettingsModal
        open={settingsModalOpen}
        onClose={handleSettingsModalClose}
        onSubmit={handleSettingsSubmit}
        submitInProgress={setSettingsStatus.loading}
        submitErrors={setSettingsStatus.errors}
        permitTypeOrgRole={selectedRole}
      />

      <AddDocumentTypeToPermitTypeModal
        open={addDocumentTypeModalOpen}
        onClose={handleAddDocumentTypeModalClose}
        onSubmit={handleAddDocumentTypes}
        submitInProgress={addDocTypeStatus.loading}
        submitErrors={addDocTypeStatus.errors}
        availableDocumentTypes={availableDocumentTypes}
      />

      <EditPermitTypeDocumentTypeSettingsModal
        open={docTypeSettingsModalOpen}
        onClose={handleDocTypeSettingsModalClose}
        onSubmit={handleDocTypeSettingsSubmit}
        submitInProgress={setDocTypeSettingsStatus.loading}
        submitErrors={setDocTypeSettingsStatus.errors}
        permitTypeDocumentType={selectedDocumentType}
      />
    </>
  );
};

export default PermitTypeViewContainer;
